import React, { useEffect, useState } from "react";
import {
    getMediaMilestones,
    sendProgressEvent,
    sendMediaEvent,
    MEDIAINFO,
    MediaType,
    IInputMediaData,
    SegmentMilestones,
} from "@telequebec/tq-googleanalytics";
import styles from "./TqVideoPlayer.module.scss";
import { swrFetcher } from "@/utils/request";
import { TYPE_GABARIT } from "@/utils/enums";
import TqImage from "@/ui/image";
import Image from "next/legacy/image";
import { Episode } from "@/types/emissions";
import { Media } from "@/types/contenu";

import ReactPlayerLoader from "@brightcove/react-player-loader";

type TqVideoPlayerProps = {
    media: Media,
    isVisuelEmissionCuisinez?: boolean;
    onEvent?: (eventName: string, args: string) => void;
};

// eslint-disable-next-line no-unused-vars
const TqVideoPlayer = ({ media, isVisuelEmissionCuisinez = false }: TqVideoPlayerProps) => {
    let milestones: SegmentMilestones[] = [];
    const [playerError, setPlayerError] = useState(false);

    const [episodeInfo, setEpisodeInfo] = useState<Episode>();

    const loadMediaInfoAsync = async (id: number) => {
        try {
            const episode = await swrFetcher(`/api/episodes/${id}`);
            if (episode?.emissionId) {
                episode.emission = await swrFetcher(`/api/emissions/${episode.emissionId}`);
                setEpisodeInfo(episode);
            }
        } catch (e) {
            console.error("Error loading media info", e);
        }
    };

    const videoProgressListener = (data: IInputMediaData, element?: HTMLMediaElement) => {
        if (element instanceof HTMLVideoElement) {
            milestones = getMediaMilestones(element);

            const handleProgress = () => {
                const newMilestones = sendProgressEvent({ milestones, media: element, data });

                if (newMilestones.toString() !== milestones.toString()) {
                    milestones = newMilestones;
                }

                if (milestones.length === 0) {
                    element.removeEventListener("timeupdate", handleProgress);
                }
            };

            element.addEventListener("timeupdate", handleProgress);
        }
    };

    const videoEvents = (e: Event) => {
        if (e.target instanceof HTMLVideoElement) {
            e.target.removeEventListener("play", videoEvents);

            const baseObj = {
                type: "VIDEO" as MediaType,
                data: {
                    // Continuité pour le BI - A modifier plus tard pour un ID Louise (ou autre)
                    item_id: media?.legacyId?.toString(),
                },
                interaction: {
                    content_provider: MEDIAINFO.DEFAULTPROVIDER,
                },
                media: e.target,
            };

            // Send playing event with 0% progress to trigger the firstStart event
            sendMediaEvent({
                mediaEvent: "playing",
                ...baseObj,
                interaction: {
                    ...baseObj.interaction,
                    content_percent: 0,
                },
            });

            e.target.addEventListener("play", () =>
                sendMediaEvent({
                    mediaEvent: "playing",
                    ...baseObj,
                }),
            );

            videoProgressListener(baseObj.data as IInputMediaData, e.target);

            e.target.addEventListener("pause", () =>
                sendMediaEvent({
                    mediaEvent: "paused",
                    ...baseObj,
                }),
            );

            e.target.addEventListener("ended", () =>
                sendMediaEvent({
                    mediaEvent: "mediaComplete",
                    ...baseObj,
                }),
            );
        }
    };

    const initVideoAnalytics = () => {
        document.querySelectorAll("#video-container video").forEach((video) => {
            video.addEventListener("play", videoEvents);
        });
    };

    useEffect(() => {
        if (isVisuelEmissionCuisinez && media?.referenceId) {
            loadMediaInfoAsync(media.referenceId);
        }
    }, [isVisuelEmissionCuisinez, media]);

    return (
        <div className={styles.me}>
            <div id="video-container" className={styles.videoContainer}>
                {media?.videoId && !playerError ? (
                    <ReactPlayerLoader
                        accountId={process.env.NEXT_PUBLIC_BRIGHTCOVE_ACCOUNTID}
                        adConfigId={process.env.NEXT_PUBLIC_BRIGHTCOVE_ADCONFIGID}
                        playerId={process.env.NEXT_PUBLIC_BRIGHTCOVE_PLAYERID}
                        videoId={media.videoId || `ref:${media.legacyId}`}
                        onSuccess={initVideoAnalytics}
                        onFailure={() => setPlayerError(true)}
                        options={{
                            preload: "metadata",
                            aspectRatio: "16:9",
                            language: "fr",
                        }}
                    />
                ) : (
                    <div className={styles["video-erreur"]}>
                        <Image
                            src="https://cdn.telequebec.tv/medias/En_direct/w1920_h1080.jpg"
                            width={640}
                            height={360}
                            layout="responsive"
                            alt="En direct - Image d'erreur de vidéo"
                        ></Image>
                        <div className={styles["video-erreur-banner"]}>
                            <p className={styles["video-erreur-message"]}>
                                Cette vidéo n&apos;est plus disponible en raison des droits qui y
                                sont associés.
                            </p>
                        </div>
                    </div>
                )}
            </div>
            {episodeInfo && (
                <div className={styles.infoEmission}>
                    <TqImage
                        gabarits={[TYPE_GABARIT.FORMAT_LOGO]}
                        images={episodeInfo.emission.images}
                        alt={episodeInfo.emission.nom}
                        className={styles.logo}
                    />

                    <div className={styles.nomEpisode}>{episodeInfo.titre}</div>
                </div>
            )}
        </div>
    );
};

export default TqVideoPlayer;
